import React, { useEffect, useState } from "react";
import TopBar from "../../components/TopBar";
import EmptyState from "../../components/EmptyState";
import { Modal, Field } from "../Fields";
import { api, fmtUSD, fmtNum, todayISO } from "../../lib/api";
import { Package, Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

const STATUSES = [
  { value: "booked", label: "Booked" },
  { value: "in_transit", label: "In Transit" },
  { value: "delivered", label: "Delivered" },
  { value: "billed", label: "Billed" },
  { value: "paid", label: "Paid" },
];

const STATUS_COLORS = {
  booked: "bg-[#F5F4F0] text-[#66645E]",
  in_transit: "bg-[#1F1E1B] text-white",
  delivered: "bg-[#BC4727] text-white",
  billed: "bg-[#1F1E1B] text-white",
  paid: "bg-[#1A4135] text-white",
};

const blank = {
  customer_id: "", commodity: "", pickup_date: "", pickup_location: "",
  delivery_date: "", delivery_location: "", miles: 0, rate_per_mile: 0, flat_rate: 0,
  status: "booked", truck: "", truck_id: "", trailer_id: "", notes: "", is_internal: false,
};

export default function Loads({ businessId }) {
  const [rows, setRows] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [trailers, setTrailers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);

  const load = async () => {
    const [r, c, t, tr] = await Promise.all([
      api.get(`/loads?business_id=${businessId}`),
      api.get(`/customers`),
      api.get(`/fleet-units?business_id=${businessId}&kind=truck`),
      api.get(`/fleet-units?business_id=${businessId}&kind=trailer`),
    ]);
    setRows(r.data); setCustomers(c.data); setTrucks(t.data); setTrailers(tr.data);
  };

  useEffect(() => { load(); }, [businessId]);

  const onSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form, business_id: businessId,
      miles: parseFloat(form.miles) || 0,
      rate_per_mile: parseFloat(form.rate_per_mile) || 0,
      flat_rate: parseFloat(form.flat_rate) || 0,
    };
    try {
      if (editing) await api.put(`/loads/${editing.id}`, payload);
      else await api.post(`/loads`, payload);
      toast.success(editing ? "Load updated" : "Load created");
      setOpen(false); setEditing(null); setForm(blank); load();
    } catch { toast.error("Could not save load"); }
  };

  const onEdit = (r) => { setEditing(r); setForm({ ...blank, ...r }); setOpen(true); };
  const onDelete = async (id) => {
    if (!window.confirm("Delete this load?")) return;
    await api.delete(`/loads/${id}`); toast.success("Deleted"); load();
  };

  const revenue = (r) => r.flat_rate > 0 ? r.flat_rate : (r.miles * r.rate_per_mile);

  return (
    <div>
      <TopBar
        title="Loads"
        subtitle="Track each haul from booking to paid."
        actions={
          <div className="flex flex-wrap items-center gap-2 mt-6">
            <button data-testid="add-load-btn" onClick={() => { setEditing(null); setForm({ ...blank, pickup_date: todayISO() }); setOpen(true); }} className="bg-[#BC4727] text-white px-5 py-2 font-bold uppercase tracking-wider text-sm hover:bg-[#A03B1F] flex items-center gap-2">
              <Plus className="w-4 h-4" /> New Load
            </button>
          </div>
        }
      />

      {rows.length === 0 ? (
        <EmptyState icon={Package} title="No loads yet" hint="Book your first haul." testid="empty-loads" />
      ) : (
        <div className="bg-white border border-[#1F1E1B] overflow-x-auto">
          <table className="ledger-table" data-testid="loads-table">
            <thead>
              <tr>
                <th>Pickup</th>
                <th>Customer</th>
                <th>Commodity</th>
                <th>Route</th>
                <th>Status</th>
                <th className="num">Miles</th>
                <th className="num">Rate</th>
                <th className="num">Revenue</th>
                <th style={{ width: 90 }}></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} data-testid={`load-row-${r.id}`}>
                  <td className="tabular">{r.pickup_date}</td>
                  <td>{r.customer_name || "—"}</td>
                  <td>{r.commodity}</td>
                  <td className="text-[#66645E] text-xs">{r.pickup_location} → {r.delivery_location}</td>
                  <td><span className={`text-[10px] font-bold uppercase tracking-[0.16em] px-1.5 py-0.5 ${STATUS_COLORS[r.status]}`}>{r.status.replace("_", " ")}</span></td>
                  <td className="num">{fmtNum(r.miles, 0)}</td>
                  <td className="num">{r.flat_rate > 0 ? `${fmtUSD(r.flat_rate)} flat` : `${fmtUSD(r.rate_per_mile)}/mi`}</td>
                  <td className="num font-bold">{fmtUSD(revenue(r))}</td>
                  <td>
                    <div className="flex gap-1.5 justify-end">
                      <button data-testid={`edit-load-${r.id}`} onClick={() => onEdit(r)} className="p-1.5 border border-[#1F1E1B] hover:bg-[#1F1E1B] hover:text-white"><Pencil className="w-3 h-3" /></button>
                      <button data-testid={`delete-load-${r.id}`} onClick={() => onDelete(r.id)} className="p-1.5 border border-[#BC4727] text-[#BC4727] hover:bg-[#BC4727] hover:text-white"><Trash2 className="w-3 h-3" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {open && (
        <Modal title={editing ? "Edit Load" : "New Load"} onClose={() => setOpen(false)}>
          <form onSubmit={onSubmit} className="space-y-4" data-testid="load-form">
            <label className="flex items-start gap-2 bg-[#FAF7EC] border-2 border-[#1F1E1B] p-3 cursor-pointer" data-testid="load-internal-toggle">
              <input
                type="checkbox"
                data-testid="load-internal-checkbox"
                checked={!!form.is_internal}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setForm({
                    ...form,
                    is_internal: checked,
                    customer_id: checked ? `internal_farm_${businessId}` : "",
                  });
                }}
                className="mt-1"
              />
              <div>
                <div className="text-xs font-bold uppercase tracking-wider">Internal haul for the Farm</div>
                <div className="text-[11px] text-[#66645E] mt-0.5">
                  Flags this haul as work done for the Farm. When invoiced, a matching Farm expense is
                  auto-created at your standard rate so the Farm books show what hauling cost would be.
                </div>
              </div>
            </label>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Customer" required>
                <select data-testid="load-customer-select" required disabled={!!form.is_internal} value={form.customer_id} onChange={(e) => setForm({ ...form, customer_id: e.target.value })} className={`w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm ${form.is_internal ? "opacity-60" : ""}`}>
                  <option value="">— Select customer —</option>
                  {customers.map((c) => <option key={c.id} value={c.id}>{c.company ? `${c.name} (${c.company})` : c.name}</option>)}
                </select>
              </Field>
              <Field label="Status">
                <select data-testid="load-status-select" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm">
                  {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Commodity" required>
                <input data-testid="load-commodity-input" required value={form.commodity} onChange={(e) => setForm({ ...form, commodity: e.target.value })} placeholder="Corn, hay, cattle, gravel…" className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" />
              </Field>
              <Field label="Truck (from Fleet)">
                <select data-testid="load-truck-select" value={form.truck_id} onChange={(e) => { const t = trucks.find((x) => x.id === e.target.value); setForm({ ...form, truck_id: e.target.value, truck: t ? t.nickname : "" }); }} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm">
                  <option value="">— None / Other —</option>
                  {trucks.map((t) => <option key={t.id} value={t.id}>{t.nickname}</option>)}
                </select>
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Trailer (from Fleet)">
                <select data-testid="load-trailer-select" value={form.trailer_id} onChange={(e) => setForm({ ...form, trailer_id: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm">
                  <option value="">— None —</option>
                  {trailers.map((t) => <option key={t.id} value={t.id}>{t.nickname}</option>)}
                </select>
              </Field>
              <Field label="Truck (free text — used if not picked from Fleet)">
                <input data-testid="load-truck-input" value={form.truck} onChange={(e) => setForm({ ...form, truck: e.target.value })} placeholder="Pete 389 #12" className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" />
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Pickup Date" required>
                <input data-testid="load-pickup-date" type="date" required value={form.pickup_date} onChange={(e) => setForm({ ...form, pickup_date: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" />
              </Field>
              <Field label="Pickup Location" required>
                <input data-testid="load-pickup-location" required value={form.pickup_location} onChange={(e) => setForm({ ...form, pickup_location: e.target.value })} placeholder="City, ST" className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" />
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Delivery Date">
                <input data-testid="load-delivery-date" type="date" value={form.delivery_date} onChange={(e) => setForm({ ...form, delivery_date: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" />
              </Field>
              <Field label="Delivery Location" required>
                <input data-testid="load-delivery-location" required value={form.delivery_location} onChange={(e) => setForm({ ...form, delivery_location: e.target.value })} placeholder="City, ST" className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" />
              </Field>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <Field label="Miles">
                <input data-testid="load-miles-input" type="number" step="0.1" value={form.miles} onChange={(e) => setForm({ ...form, miles: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm tabular" />
              </Field>
              <Field label="$ / mile">
                <input data-testid="load-rate-per-mile" type="number" step="0.01" value={form.rate_per_mile} onChange={(e) => setForm({ ...form, rate_per_mile: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm tabular" />
              </Field>
              <Field label="Flat Rate (overrides $/mi)">
                <input data-testid="load-flat-rate" type="number" step="0.01" value={form.flat_rate} onChange={(e) => setForm({ ...form, flat_rate: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm tabular" />
              </Field>
            </div>
            <Field label="Notes">
              <textarea data-testid="load-notes-input" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" />
            </Field>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" data-testid="load-cancel-btn" onClick={() => setOpen(false)} className="border border-[#1F1E1B] px-5 py-2 text-sm font-bold uppercase tracking-wider">Cancel</button>
              <button type="submit" data-testid="load-submit-btn" className="bg-[#1A4135] text-white px-5 py-2 text-sm font-bold uppercase tracking-wider hover:bg-[#133128]">Save</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
